import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Container,
  Card,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getFhsysUsers, FhsysUser, FhsysUsersResponse } from "../../services/api";
import { useErrorStore } from "../../errorStore";
import { UserContext } from "../../userContext";
import { Search } from "react-bootstrap-icons";

export default function FhsysUsers() {
  const { t } = useTranslation("common");
  const [users, setUsers] = useState<FhsysUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [pagination, setPagination] = useState<
    FhsysUsersResponse["pagination"] | null
  >(null);
  const setErrorMessage = useErrorStore((state) => state.setErrorMessage);
  const currentUser = useContext(UserContext);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadUsers = async (page: number = 1, search?: string) => {
    try {
      setLoading(true);
      const response = await getFhsysUsers(page, perPage, search, roleFilter);
      setUsers(response.data);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error: any) {
      setErrorMessage(t("fhsysUsers.loadError"));
      console.error("Error loading FHsys users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, roleFilter]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setSearchTerm(value);
      loadUsers(1, value);
    }, 400);
  };

  const handlePageChange = (page: number) => {
    loadUsers(page, searchTerm);
  };

  if (currentUser?.role !== "admin") {
    return (
      <Container>
        <Alert variant="danger">{t("accessDenied")}</Alert>
      </Container>
    );
  }

  const renderRoleBadge = (role: string) => {
    const variant =
      role === "office" ? "success" : role === "teacher" ? "primary" : "secondary";
    return <Badge bg={variant}>{role}</Badge>;
  };

  const renderMoodle = (
    user: FhsysUser,
    field: "role" | "addtlrole" | "DLID" | "FLID"
  ) => {
    if (!user.moodle) {
      return (
        <Badge bg="light" text="dark">
          {t("fhsysUsers.notInMoodle")}
        </Badge>
      );
    }
    const value = user.moodle[field];
    if (!value) {
      return <span className="text-muted">-</span>;
    }
    return <code>{value}</code>;
  };

  const renderList = (values: string[]) => {
    if (!values.length) {
      return <span className="text-muted">-</span>;
    }
    return (
      <div className="d-flex gap-1 flex-wrap">
        {values.map((value) => (
          <Badge bg="light" text="dark" key={value}>
            {value}
          </Badge>
        ))}
      </div>
    );
  };

  return (
    <Container fluid>
      <div className="d-flex flex-wrap flex-md-nowrap align-items-center pt-3 pb-3 mb-3 border-bottom">
        <h1 className="h2">{t("fhsysUsers.title")}</h1>
      </div>


      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={5}>
              <Form.Label>{t("fhsysUsers.searchLabel")}</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t("fhsysUsers.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label>{t("fhsysUsers.roleLabel")}</Form.Label>
              <Form.Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">{t("fhsysUsers.roleAll")}</option>
                <option value="office">office</option>
                <option value="teacher">teacher</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>{t("fhsysUsers.perPageLabel")}</Form.Label>
              <Form.Select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <div className="text-muted">
                {t("fhsysUsers.totalCount", { count: pagination?.total ?? 0 })}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>{t("fhsysUsers.name")}</th>
                  <th>{t("fhsysUsers.username")}</th>
                  <th>{t("fhsysUsers.employeeTypes")}</th>
                  <th>{t("fhsysUsers.departments")}</th>
                  <th>{t("fhsysUsers.officeDepartments")}</th>
                  <th>{t("fhsysUsers.moodleRole")}</th>
                  <th>{t("fhsysUsers.moodleAddtlrole")}</th>
                  <th>{t("fhsysUsers.moodleDlid")}</th>
                  <th>{t("fhsysUsers.moodleFlid")}</th>
                  <th>{t("fhsysUsers.role")}</th>
                  <th>{t("fhsysUsers.effectiveRole")}</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.username}>
                      <td>
                        {user.lastname} {user.firstname}
                      </td>
                      <td>
                        <code>{user.username}</code>
                      </td>
                      <td>{renderList(user.employeeTypes)}</td>
                      <td>{renderList(user.departments)}</td>
                      <td>
                        {renderList(user.officeDepartmentIds)}
                        {user.unmappedOfficeCodes?.length > 0 && (
                          <Badge bg="warning" text="dark">
                            {t("fhsysUsers.unmapped", {
                              codes: user.unmappedOfficeCodes.join(", "),
                            })}
                          </Badge>
                        )}
                      </td>
                      <td>{renderRoleBadge(user.role)}</td>
                      <td>
                        {renderRoleBadge(user.effectiveRole)}
                        {user.effectiveRole !== user.role && (
                          <Badge bg="info" text="dark" className="ms-1">
                            {t("fhsysUsers.viaMoodle")}
                          </Badge>
                        )}
                      </td>
                      <td>{renderMoodle(user, "role")}</td>
                      <td>{renderMoodle(user, "addtlrole")}</td>
                      <td>{renderMoodle(user, "DLID")}</td>
                      <td>{renderMoodle(user, "FLID")}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={11} className="text-center text-muted py-4">
                      {t("fhsysUsers.noResults")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {pagination && pagination.total_pages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {(() => {
              const pages = [];
              const maxPages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
              let endPage = Math.min(
                pagination.total_pages,
                startPage + maxPages - 1
              );
              if (endPage - startPage < maxPages - 1) {
                startPage = Math.max(1, endPage - maxPages + 1);
              }
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                  >
                    {i}
                  </Pagination.Item>
                );
              }
              return pages;
            })()}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(pagination.total_pages)}
              disabled={currentPage === pagination.total_pages}
            />
          </Pagination>
        </div>
      )}
    </Container>
  );
}
